import { useState, useEffect } from 'react';
import { Globe, Check, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { setLanguage, getSavedLanguage, applySavedLanguage } from '@/lib/googleTranslate';

const languages = [
  { code: 'en', nativeLabel: 'English' },
  { code: 'af', nativeLabel: 'Afrikaans' },
  { code: 'sq', nativeLabel: 'Shqip' },
  { code: 'am', nativeLabel: '\u12A0\u121B\u122D\u129B' },
  { code: 'ar', nativeLabel: '\u0627\u0644\u0639\u0631\u0628\u064A\u0629' },
  { code: 'hy', nativeLabel: '\u0540\u0561\u0575\u0565\u0580\u0565\u0576' },
  { code: 'az', nativeLabel: 'Az\u0259rbaycan' },
  { code: 'eu', nativeLabel: 'Euskara' },
  { code: 'be', nativeLabel: '\u0411\u0435\u043B\u0430\u0440\u0443\u0441\u043A\u0430\u044F' },
  { code: 'bn', nativeLabel: '\u09AC\u09BE\u0982\u09B2\u09BE' },
  { code: 'bs', nativeLabel: 'Bosanski' },
  { code: 'bg', nativeLabel: '\u0411\u044A\u043B\u0433\u0430\u0440\u0441\u043A\u0438' },
  { code: 'ca', nativeLabel: 'Catal\u00E0' },
  { code: 'ceb', nativeLabel: 'Cebuano' },
  { code: 'zh-CN', nativeLabel: '\u4E2D\u6587(\u7B80\u4F53)' },
  { code: 'zh-TW', nativeLabel: '\u4E2D\u6587(\u7E41\u9AD4)' },
  { code: 'co', nativeLabel: 'Corsu' },
  { code: 'hr', nativeLabel: 'Hrvatski' },
  { code: 'cs', nativeLabel: '\u010Ce\u0161tina' },
  { code: 'da', nativeLabel: 'Dansk' },
  { code: 'nl', nativeLabel: 'Nederlands' },
  { code: 'eo', nativeLabel: 'Esperanto' },
  { code: 'et', nativeLabel: 'Eesti' },
  { code: 'fi', nativeLabel: 'Suomi' },
  { code: 'fr', nativeLabel: 'Fran\u00E7ais' },
  { code: 'fy', nativeLabel: 'Frysk' },
  { code: 'gl', nativeLabel: 'Galego' },
  { code: 'ka', nativeLabel: '\u10E5\u10D0\u10E0\u10D7\u10E3\u10DA\u10D8' },
  { code: 'de', nativeLabel: 'Deutsch' },
  { code: 'el', nativeLabel: '\u0395\u03BB\u03BB\u03B7\u03BD\u03B9\u03BA\u03AC' },
  { code: 'gu', nativeLabel: '\u0A97\u0AC1\u0A9C\u0AB0\u0ABE\u0AA4\u0AC0' },
  { code: 'ht', nativeLabel: 'Krey\u00F2l Ayisyen' },
  { code: 'ha', nativeLabel: 'Hausa' },
  { code: 'haw', nativeLabel: '\u02BB\u014Clelo Hawai\u02BBi' },
  { code: 'iw', nativeLabel: '\u05E2\u05D1\u05E8\u05D9\u05EA' },
  { code: 'hi', nativeLabel: '\u0939\u093F\u0928\u094D\u0926\u0940' },
  { code: 'hmn', nativeLabel: 'Hmong' },
  { code: 'hu', nativeLabel: 'Magyar' },
  { code: 'is', nativeLabel: '\u00CDslenska' },
  { code: 'ig', nativeLabel: 'Igbo' },
  { code: 'id', nativeLabel: 'Bahasa Indonesia' },
  { code: 'ga', nativeLabel: 'Gaeilge' },
  { code: 'it', nativeLabel: 'Italiano' },
  { code: 'ja', nativeLabel: '\u65E5\u672C\u8A9E' },
  { code: 'jw', nativeLabel: 'Jawa' },
  { code: 'kn', nativeLabel: '\u0C95\u0CA8\u0CCD\u0CA8\u0CA1' },
  { code: 'kk', nativeLabel: '\u049A\u0430\u0437\u0430\u049B' },
  { code: 'km', nativeLabel: '\u1781\u17D2\u1798\u17C2\u179A' },
  { code: 'rw', nativeLabel: 'Kinyarwanda' },
  { code: 'ko', nativeLabel: '\uD55C\uAD6D\uC5B4' },
  { code: 'ku', nativeLabel: 'Kurd\u00EE' },
  { code: 'ky', nativeLabel: '\u041A\u044B\u0440\u0433\u044B\u0437\u0447\u0430' },
  { code: 'lo', nativeLabel: '\u0EA5\u0EB2\u0EA7' },
  { code: 'la', nativeLabel: 'Latina' },
  { code: 'lv', nativeLabel: 'Latvie\u0161u' },
  { code: 'lt', nativeLabel: 'Lietuvi\u0173' },
  { code: 'lb', nativeLabel: 'L\u00EBtzebuergesch' },
  { code: 'mk', nativeLabel: '\u041C\u0430\u043A\u0435\u0434\u043E\u043D\u0441\u043A\u0438' },
  { code: 'mg', nativeLabel: 'Malagasy' },
  { code: 'ms', nativeLabel: 'Bahasa Melayu' },
  { code: 'ml', nativeLabel: '\u0D2E\u0D32\u0D2F\u0D3E\u0D33\u0D02' },
  { code: 'mt', nativeLabel: 'Malti' },
  { code: 'mi', nativeLabel: 'M\u0101ori' },
  { code: 'mr', nativeLabel: '\u092E\u0930\u093E\u0920\u0940' },
  { code: 'mn', nativeLabel: '\u041C\u043E\u043D\u0433\u043E\u043B' },
  { code: 'my', nativeLabel: '\u1019\u103C\u1014\u103A\u1019\u102C' },
  { code: 'ne', nativeLabel: '\u0928\u0947\u092A\u093E\u0932\u0940' },
  { code: 'no', nativeLabel: 'Norsk' },
  { code: 'ny', nativeLabel: 'Chichewa' },
  { code: 'or', nativeLabel: '\u0B13\u0B21\u0B3C\u0B3F\u0B06' },
  { code: 'ps', nativeLabel: '\u067E\u069A\u062A\u0648' },
  { code: 'fa', nativeLabel: '\u0641\u0627\u0631\u0633\u06CC' },
  { code: 'pl', nativeLabel: 'Polski' },
  { code: 'pt', nativeLabel: 'Portugu\u00EAs' },
  { code: 'pa', nativeLabel: '\u0A2A\u0A70\u0A1C\u0A3E\u0A2C\u0A40' },
  { code: 'ro', nativeLabel: 'Rom\u00E2n\u0103' },
  { code: 'ru', nativeLabel: '\u0420\u0443\u0441\u0441\u043A\u0438\u0439' },
  { code: 'sm', nativeLabel: 'Gagana S\u0101moa' },
  { code: 'gd', nativeLabel: 'G\u00E0idhlig' },
  { code: 'sr', nativeLabel: '\u0421\u0440\u043F\u0441\u043A\u0438' },
  { code: 'st', nativeLabel: 'Sesotho' },
  { code: 'sn', nativeLabel: 'Shona' },
  { code: 'sd', nativeLabel: '\u0633\u0646\u068C\u064A' },
  { code: 'si', nativeLabel: '\u0DC3\u0DD2\u0D82\u0DC4\u0DBD' },
  { code: 'sk', nativeLabel: 'Sloven\u010Dina' },
  { code: 'sl', nativeLabel: 'Sloven\u0161\u010Dina' },
  { code: 'so', nativeLabel: 'Soomaali' },
  { code: 'es', nativeLabel: 'Espa\u00F1ol' },
  { code: 'su', nativeLabel: 'Basa Sunda' },
  { code: 'sw', nativeLabel: 'Kiswahili' },
  { code: 'sv', nativeLabel: 'Svenska' },
  { code: 'tl', nativeLabel: 'Filipino' },
  { code: 'tg', nativeLabel: '\u0422\u043E\u04B7\u0438\u043A\u04E3' },
  { code: 'ta', nativeLabel: '\u0BA4\u0BAE\u0BBF\u0BB4\u0BCD' },
  { code: 'tt', nativeLabel: '\u0422\u0430\u0442\u0430\u0440' },
  { code: 'te', nativeLabel: '\u0C24\u0C46\u0C32\u0C41\u0C17\u0C41' },
  { code: 'th', nativeLabel: '\u0E44\u0E17\u0E22' },
  { code: 'tr', nativeLabel: 'T\u00FCrk\u00E7e' },
  { code: 'tk', nativeLabel: 'T\u00FCrkmen' },
  { code: 'uk', nativeLabel: '\u0423\u043A\u0440\u0430\u0457\u043D\u0441\u044C\u043A\u0430' },
  { code: 'ur', nativeLabel: '\u0627\u0631\u062F\u0648' },
  { code: 'ug', nativeLabel: '\u0626\u06C7\u064A\u063A\u06C7\u0631\u0686\u06D5' },
  { code: 'uz', nativeLabel: "O'zbek" },
  { code: 'vi', nativeLabel: 'Ti\u1EBFng Vi\u1EC7t' },
  { code: 'cy', nativeLabel: 'Cymraeg' },
  { code: 'xh', nativeLabel: 'isiXhosa' },
  { code: 'yi', nativeLabel: '\u05D9\u05D9\u05B4\u05D3\u05D9\u05E9' },
  { code: 'yo', nativeLabel: 'Yor\u00F9b\u00E1' },
  { code: 'zu', nativeLabel: 'isiZulu' },
];

const LanguageSwitcher = () => {
  const [currentLang, setCurrentLang] = useState(getSavedLanguage);
  const [search, setSearch] = useState('');

  // Apply saved language on mount (after Google Translate widget loads)
  useEffect(() => {
    applySavedLanguage();
  }, []);

  const changeLanguage = (langCode: string) => {
    setCurrentLang(langCode);
    setLanguage(langCode);
  };

  const filteredLanguages = languages.filter((lang) =>
    lang.nativeLabel.toLowerCase().includes(search.toLowerCase()) ||
    lang.code.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 border-border bg-card hover:bg-muted"
        >
          <Globe className="h-4 w-4 text-slate-500" />
          <span className="sr-only">Language</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuLabel className="flex items-center gap-2">
          <Globe className="w-4 h-4" />
          Language
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="px-2 pb-2">
          <div className="flex items-center gap-2 px-2 py-1.5 border border-slate-200 dark:border-slate-700 rounded-md">
            <Search className="w-3.5 h-3.5 text-slate-400" />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full text-sm bg-transparent outline-none placeholder:text-slate-400"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto">
          {filteredLanguages.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className="flex items-center justify-between cursor-pointer"
            >
              <span>{lang.nativeLabel}</span>
              {currentLang === lang.code && (
                <Check className="w-4 h-4 text-blue-500" />
              )}
            </DropdownMenuItem>
          ))}
          {filteredLanguages.length === 0 && (
            <div className="px-2 py-3 text-sm text-center text-slate-400">
              No language found
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
